import { Button, Grid } from "@mui/material";
import { useFormik } from "formik";
import { useMemo, useState } from "react";
import * as Yup from "yup";
import {
  addDepartmentInitialValues,
  DepartmentInitialValuesType,
} from "../../../../constants/ProviderGroupFormInitialValues";
import Input from "../../../Input";
import { useAddDepartMent } from "../../../../queries/use-add-department";
import { useEditDepartMent } from "../../../../queries/use-edit-department";
import { useLocationsList } from "../../../../queries/use-locationList";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useUsersList } from "../../../../queries/use-get-users";
import { CustomSelectScroll } from "../../../CustomSelectScroll";
import SideSheetDrawer from "../../../SidesheetDrawer";
import { useDebounce } from "../../../../hooks/use-debounce";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getSuccessSnackbar,
  getUnNecessaryTransformData,
} from "../../../../utils/utils";
import MaskInput from "../../../MaskInput";
import MaskMultiSelectScroll from "../../../MaskMultiSelectScroll";
import Text from "../../../Text";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import { getViewAs, getStoredRealm } from "@/utils/auth-storage";

interface DepartmentModalProps {
  open: boolean;
  modalTitle: string;
  onClose: () => void;
  preFillValues?: DepartmentInitialValuesType;
  xTENANTID?: string;
}

const departmentValidationSchema = Yup.object({
  name: Yup.string().required().label("Department Name"),
  contact: Yup.string().min(12, "Contact Number must be at least 8 digits"),
  // workLocations: Yup.array()
  //   .min(1, "Locations is a required field")
  //   .required()
  //   .label("Location"),
});

const DepartmentDrawer = ({
  open,
  modalTitle: dialogTitle,
  onClose,
  preFillValues,
  xTENANTID,
}: DepartmentModalProps) => {
  const {
    values,
    setFieldValue,
    handleBlur,
    handleChange,
    resetForm,
    handleSubmit,
    errors,
    touched,
  } = useFormik({
    initialValues: preFillValues ?? addDepartmentInitialValues,
    validationSchema: departmentValidationSchema,
    onSubmit,
    enableReinitialize: true,
  });
  const viewAs = getViewAs();
  const realm: string = getStoredRealm() ?? "";
  const params = useParams();

  const [adminSearchKey, setAdminSearchKey] = useDebounce<string>("");
  const [adminPagination, setAdminPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);

  const { data: UsersList, isFetching: isUsersFetching } = useUsersList({
    pagination: adminPagination,
    searchKey: adminSearchKey,
    providerGroupId:
      viewAs === "provider" ? realm : (params?.providerGroupId ?? undefined),
    statusKey: true,
  });

  const [locationPagination, setLocationPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: providerGroupLocationList, isFetching: isLocationFetching } =
    useLocationsList({
      status: true,
      pagination: locationPagination,
      providerGroupId:
        viewAs === "provider" ? realm : (params?.providerGroupId ?? undefined),
    });

  const locationOptions = useMemo(() => {
    const fetched =
      providerGroupLocationList?.data?.content?.map((l) => ({
        uuid: l?.uuid ?? "",
        name: l?.name ?? "",
      })) ?? [];
    const selected = (values.workLocations ?? []) as { [k: string]: string }[];
    const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
    const missing = selected.filter(
      (s) => !fetched.some((o) => o[idKey as keyof typeof o] === s[idKey])
    );
    return [...missing, ...fetched];
  }, [providerGroupLocationList, values.workLocations]);

  const { mutateAsync: addDepartMent } = useAddDepartMent();
  const { mutateAsync: editDepartMent } = useEditDepartMent();

  const client = useQueryClient();
  const showSnackbar = useSnackbar();

  function onSubmit(values: DepartmentInitialValuesType) {
    const transFormData: any = getUnNecessaryTransformData(
      ["workLocations"],
      values
    );
    const payload = { ...transFormData, xTENANTID: xTENANTID };

    if (preFillValues) {
      editDepartMent(payload as any)
        .then((res) => {
          showSnackbar(getSuccessSnackbar(res));

          setTimeout(() => {
            client.invalidateQueries({
              queryKey: ["provider-group-departments-list"],
            });
            resetForm();
            onClose();
          }, 0);
        })
        .catch((err) => {
          showSnackbar(getErrorSnackbar(err));
        });
      return;
    }

    addDepartMent(payload as any)
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));

        setTimeout(() => {
          resetForm();
          onClose();
          client.invalidateQueries({
            queryKey: ["provider-group-departments-list"],
          });
        }, 0);
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  }

  return (
    <SideSheetDrawer
      onClose={() => {
        onClose();
        resetForm();
      }}
      open={open}
      title={dialogTitle}
      w="50vw"
      footerActions={
        <Button onClick={() => handleSubmit()} size="large" variant="contained">
          <Text>Save</Text>
        </Button>
      }
    >
      <Grid container width="100%" spacing={2}>
        <Grid item lg={6} xs={12}>
          <Input
            label="Department ID"
            textFieldProps={{
              name: "deptId",
              value: values.deptId,
              onChange: handleChange,
              onBlur: handleBlur,
              placeholder: "Enter Department ID",
            }}
          />
        </Grid>
        <Grid item lg={6} xs={12}>
          <Input
            label="Department Name"
            textFieldProps={{
              name: "name",
              value: values.name,
              onChange: handleChange,
              onBlur: handleBlur,
              placeholder: "Enter Department Name",
            }}
            required
            error={touched?.name && errors?.name}
          />
        </Grid>
        <Grid item lg={6} xs={12}>
          <CustomSelectScroll
            label="Department Admin"
            options={
              UsersList?.data?.content?.map((el) => {
                return {
                  value: el.uuid,
                  label: el.firstName + " " + el.lastName,
                };
              }) ?? []
            }
            value={values.adminId ?? null}
            onChange={(newOption) => {
              setFieldValue("adminId", newOption);
            }}
            onSearch={(_, searchText) => {
              setAdminSearchKey(searchText);
              setAdminPagination((p) => ({ ...p, page: 0 }));
            }}
            placeholder="Select Department Admin"
            pagination={adminPagination}
            setPagination={setAdminPagination}
            totalElements={UsersList?.data?.page?.totalElements}
            loading={isUsersFetching}
          />
        </Grid>
        <Grid item lg={6} xs={12}>
          <MaskInput
            mask={"(999) 999-9999"}
            label="Contact Number"
            name="contact"
            error={errors.contact}
            placeholder="Enter Contact Number"
            value={values.contact}
            onChange={(e) => setFieldValue("contact", e.target.value)}
          />
        </Grid>
        <Grid item lg={6} xs={12}>
          <MaskMultiSelectScroll
            // required
            label="Location"
            options={locationOptions}
            optionWithSelectAll
            optionWithCheckbox={true}
            value={values.workLocations}
            onChange={(v) => setFieldValue("workLocations", v)}
            pagination={locationPagination}
            setPagination={setLocationPagination}
            totalElements={providerGroupLocationList?.data?.page?.totalElements}
            loading={isLocationFetching}
            // error={touched?.workLocations && errors?.workLocations}
          />
        </Grid>
      </Grid>
    </SideSheetDrawer>
  );
};

export default DepartmentDrawer;
